import { useCallback, useEffect, useMemo } from 'react';
import find from 'lodash/find';
import isArray from 'lodash/isArray';

import { FetchCacheItemTagsFilters, ItemTagID } from '../../itemTagsTypes';
import {
  MultiSelectChangeCallbackType,
  MultiSelectDataType
} from '../../../../helpers/MultiSelect/types';

import {
  FETCH_CACHE_ITEM_TAGS_QUERY,
  FetchCacheItemTagsQueryResponse
} from '../../queries/fetchCacheItemTags.query';

import { usePreviousValue } from '../../../../common/hooks/usePreviousValue';
import { useCachePaginatedItemTags } from '../../hooks/useCachePaginatedItemTags';

import {
  filterConfigOnlyLabels,
  MultiSelect,
  MultiSelectProps
} from '../../../../helpers/MultiSelect';
import { ItemTagCache } from '../../ItemTagCache';

const initialLimit = 100;

interface TagsMultiSelectProps {
  value: ItemTagID[];
  autoFocus?: boolean;
  onChange: (value: ItemTagID[]) => void;
  initialFilters: FetchCacheItemTagsFilters;
  sortable?: boolean;
}

function ItemTagsMultiSelect({
  value,
  autoFocus,
  onChange,
  initialFilters,
  sortable
}: TagsMultiSelectProps & MultiSelectProps) {
  const { itemTags, itemTagsError, itemTagsLoading, filterItemTags } =
    useCachePaginatedItemTags<FetchCacheItemTagsQueryResponse>({
      cacheKey: ItemTagCache.selectFieldCacheKey(),
      initialLimit: initialLimit,
      initialPageFilters: initialFilters,
      query: FETCH_CACHE_ITEM_TAGS_QUERY,
      options: {
        withoutPrefetch: true
      }
    });

  const prevFilters = usePreviousValue(initialFilters);

  useEffect(() => {
    if (prevFilters !== initialFilters) {
      filterItemTags(initialFilters);
    }
  }, [filterItemTags, initialFilters, prevFilters]);

  const itemTagsData = useMemo<MultiSelectDataType[]>(() => {
    return itemTags.map((item) => ({
      label: item.name,
      value: item.id
    }));
  }, [itemTags]);

  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (value: MultiSelectDataType) => {
      onChange(isArray(value) ? value.map((item) => item.value) : []);
    },
    [onChange]
  );

  const multiSelectValue = useMemo<MultiSelectDataType[]>(() => {
    return isArray(value)
      ? value.map((item) => find(itemTagsData, ['value', item]))
      : [];
  }, [value, itemTagsData]);

  return (
    <MultiSelect
      error={itemTagsError}
      inputWrapperClassName="block"
      onChange={handleChange}
      value={multiSelectValue}
      multi={true}
      isSearchable={true}
      optionsLoading={itemTagsLoading}
      isLoading={itemTagsLoading}
      data={itemTagsData}
      classNamePrefix="av_multi_select"
      autoFocus={autoFocus}
      sortable={sortable}
      filterConfig={filterConfigOnlyLabels}
    />
  );
}

export default ItemTagsMultiSelect;
